/**
 * PermissionDenied — centered "not authorized" card.
 *
 * Visual matches UnauthorizedBanner (lock emoji + title + subtitle in a
 * surfaced card). Keeps the original props so existing call sites
 * (FeatureGuard, AgentStudioPage, ProjectsPage, TableOntologyPage) keep
 * working unchanged.
 */

import React from 'react'
import { ERROR_MESSAGES } from '../constants/errorMessages'

export interface PermissionDeniedProps {
  /** The action that was denied (e.g., "update", "delete", "view") */
  action?: 'update' | 'delete' | 'view' | 'create'
  /** The resource type (e.g., "model", "prompt", "agent") */
  resource?: string
  /** Custom message to display */
  message?: string
  /** Whether to show as inline component (no centering, smaller padding) */
  inline?: boolean
  /** Custom CSS class */
  className?: string
  /** Callback when user clicks to request access */
  onRequestAccess?: () => void
}

const resolveMessage = (
  action: PermissionDeniedProps['action'],
  resource?: string,
  message?: string,
): string => {
  if (message) return message
  if (!resource) return ERROR_MESSAGES.PERMISSION_DENIED
  switch (action) {
    case 'update':
      return ERROR_MESSAGES.PERMISSION_DENIED_UPDATE(resource)
    case 'delete':
      return ERROR_MESSAGES.PERMISSION_DENIED_DELETE(resource)
    case 'create':
      return ERROR_MESSAGES.PERMISSION_DENIED_CREATE(resource)
    case 'view':
    default:
      return ERROR_MESSAGES.PERMISSION_DENIED_VIEW(resource)
  }
}

export const PermissionDenied: React.FC<PermissionDeniedProps> = ({
  action = 'view',
  resource,
  message,
  inline = false,
  className = '',
  onRequestAccess,
}) => {
  const subtitle = resolveMessage(action, resource, message)

  const wrapperStyle: React.CSSProperties = inline
    ? { display: 'flex', justifyContent: 'center', padding: '1rem' }
    : {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        padding: '2rem',
      }

  const cardStyle: React.CSSProperties = {
    textAlign: 'center',
    maxWidth: 480,
    padding: inline ? '1.5rem' : '2.5rem',
    borderRadius: 12,
    border: '1px solid var(--color-border, #e5e7eb)',
    background: 'var(--color-surface, #fff)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
  }

  return (
    <div style={wrapperStyle} className={className}>
      <div style={cardStyle} role="alert">
        <div style={{ fontSize: inline ? 32 : 40, marginBottom: '1rem' }} aria-hidden>🔒</div>
        <h2
          style={{
            fontSize: '1.25rem',
            fontWeight: 600,
            color: 'var(--color-text-primary, #111827)',
            margin: 0,
            marginBottom: '0.5rem',
          }}
        >
          You are not Authorized to view this Page
        </h2>
        <p
          style={{
            color: 'var(--color-text-secondary, #6b7280)',
            fontSize: '0.9375rem',
            margin: 0,
          }}
        >
          {subtitle}
        </p>
        {onRequestAccess && (
          <button
            type="button"
            onClick={onRequestAccess}
            style={{
              marginTop: '1.25rem',
              padding: '0.5rem 1rem',
              fontSize: '0.875rem',
              fontWeight: 500,
              borderRadius: 8,
              border: '1px solid var(--color-border, #e5e7eb)',
              background: 'var(--color-surface, #fff)',
              color: 'var(--color-text-primary, #111827)',
              cursor: 'pointer',
            }}
          >
            Request Access
          </button>
        )}
      </div>
    </div>
  )
}

export default PermissionDenied
